import { Controller } from "@hotwired/stimulus"

// Toggle-Dropdown für gespeicherte Filter-Ansichten (Custom Views) +
// Save/Reset/Delete Aktionen via Backend-Endpoints.
export default class extends Controller {
  static targets = ["dropdown"]
  static values = { saveUrl: String, deleteUrl: String, baseUrl: String }

  connect() {
    this._onClickOutside = (e) => {
      if (!this.element.contains(e.target)) this.close()
    }
    document.addEventListener("click", this._onClickOutside)

    // view_id aus der URL in alle Filter-Forms (search_form_for) als hidden
    // Field injizieren, damit beim Filter-Submit die aktive View nicht
    // verloren geht. Server setzt @active_custom_view nur wenn view_id
    // wieder im Request steht — sonst verschwindet der „Aktualisieren"-Button
    // nach jeder Filter-Aenderung.
    this._persistViewIdIntoForms()
  }

  _persistViewIdIntoForms() {
    const params = new URLSearchParams(window.location.search)
    const viewId = params.get("view_id")
    if (!viewId) return

    document.querySelectorAll('form[action*="/backend/"]').forEach((form) => {
      if (form.querySelector('input[name="view_id"]')) return
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "view_id"
      input.value = viewId
      form.appendChild(input)
    })
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle("hidden")
    }
  }

  close() {
    if (this.hasDropdownTarget) this.dropdownTarget.classList.add("hidden")
  }

  // Aktualisiert eine bestehende View (id aus dataset) mit den aktuellen
  // Filtern, ohne neuen Namen abzufragen. Klick: 'X aktualisieren'.
  async update(event) {
    event.preventDefault()
    event.stopPropagation()
    const viewId = event.currentTarget.dataset.viewId
    const viewName = event.currentTarget.dataset.viewName
    if (!viewId) return
    if (!confirm(`„${viewName}" mit den aktuellen Filtern überschreiben?`)) return
    await this._submitSave({ view_id: viewId })
  }

  async save(event) {
    event.preventDefault()
    event.stopPropagation()
    const result = await this.askName()
    if (!result || !result.name || !result.name.trim()) return
    const payload = { name: result.name.trim() }
    if (result.shared) payload.shared = true
    await this._submitSave(payload)
  }

  // Kopiert einen Link zur gespeicherten Ansicht in die Zwischenablage,
  // damit der Recruiter ihn per Mail/Slack/Teams teilen kann. URL enthaelt
  // ?view_id=<id>, der Empfaenger sieht die View nur, wenn sie shared=true ist
  // ODER er sie selbst angelegt hat — sonst landet er auf einer leeren Liste.
  async copyLink(event) {
    event.preventDefault()
    event.stopPropagation()
    const viewId = event.currentTarget.dataset.viewId
    if (!viewId) return
    const base = this.baseUrlValue || window.location.pathname
    const url = new URL(base, window.location.origin)
    url.searchParams.set("view_id", viewId)
    try {
      await navigator.clipboard.writeText(url.toString())
      this._toast("Link in Zwischenablage kopiert")
    } catch {
      alert("Link konnte nicht kopiert werden. Bitte manuell kopieren: " + url.toString())
    }
  }

  // Mini-Toast unten rechts, 2s sichtbar. Mehrere Toasts stapeln sich
  // nicht — der letzte ersetzt den davorigen.
  _toast(message) {
    let toast = document.getElementById("filter-presets-toast")
    if (!toast) {
      toast = document.createElement("div")
      toast.id = "filter-presets-toast"
      toast.className = "fixed bottom-6 right-6 z-50 rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white shadow-lg transition-opacity duration-200"
      document.body.appendChild(toast)
    }
    toast.textContent = message
    toast.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => {
      toast.style.opacity = "0"
      setTimeout(() => toast.remove(), 250)
    }, 2000)
  }

  // Gemeinsame Save-Logik fuer save() und update().
  // extra = { name } fuer neu, oder { view_id } fuer Update.
  async _submitSave(extra) {
    const csrf =
      document.querySelector("meta[name='csrf-token']")?.content || ""
    const params = new URLSearchParams(window.location.search)
    const q = {}
    let scope = "all"
    for (const [key, value] of params.entries()) {
      const m = key.match(/^q\[(.+)\]$/)
      if (m) q[m[1]] = value
      else if (key === "scope") scope = value
    }

    try {
      const res = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          Accept: "application/json"
        },
        body: JSON.stringify({ scope, q, ...extra })
      })
      const data = await res.json()
      if (data.ok) {
        // Direkt in die gespeicherte/aktualisierte View springen.
        const base = this.baseUrlValue || window.location.pathname
        const search = new URLSearchParams(window.location.search)
        search.set("view_id", data.id)
        window.location.assign(`${base}?${search.toString()}`)
      } else {
        alert(data.error || "Fehler beim Speichern")
      }
    } catch {
      alert("Fehler beim Speichern")
    }
  }

  async remove(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const name = btn.dataset.presetName || "Ansicht"
    const isShared = btn.dataset.presetShared === "true"
    const message = isShared
      ? `„${name}" ist mit allen geteilt. Wirklich löschen?`
      : `„${name}" löschen?`
    if (!confirm(message)) return

    const id = btn.dataset.presetId
    const csrf =
      document.querySelector("meta[name='csrf-token']")?.content || ""
    const url = this.deleteUrlValue.replace("__ID__", id)

    try {
      await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" }
      })
      window.location.reload()
    } catch {
      alert("Fehler beim Löschen")
    }
  }

  reset(event) {
    event.preventDefault()
    const scope =
      new URLSearchParams(window.location.search).get("scope") || "all"
    const base = this.baseUrlValue || window.location.pathname
    window.location.assign(`${base}?scope=${scope}`)
  }

  // Modal-Dialog statt window.prompt. Liefert { name, shared } zurueck.
  // 'shared'-Checkbox nur fuer Admins sichtbar (data-can-share auf dem
  // Controller-Element gesetzt vom Server).
  askName() {
    return new Promise((resolve) => {
      const canShare = this.element.dataset.canShare === "true"
      const dialog = document.createElement("dialog")
      dialog.className = "rounded-lg shadow-xl border border-gray-200 p-0 backdrop:bg-black backdrop:bg-opacity-30"
      dialog.innerHTML = `
        <form method="dialog" class="w-80 p-5">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Filter speichern</h3>
          <label class="block text-xs font-medium text-gray-700 mb-1">Name</label>
          <input type="text" name="preset-name" autocomplete="off" required
                 class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                 placeholder="z. B. Aktive Bewerber Bayreuth">
          ${canShare ? `
            <label class="mt-3 flex items-center gap-2 text-xs text-gray-700">
              <input type="checkbox" name="preset-shared" class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
              <span>Für alle Benutzer sichtbar</span>
            </label>
          ` : ""}
          <div class="flex justify-end gap-2 mt-4">
            <button type="button" data-action="cancel" class="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">Abbrechen</button>
            <button type="submit" class="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md">Speichern</button>
          </div>
        </form>
      `
      document.body.appendChild(dialog)
      const input = dialog.querySelector("input[name='preset-name']")
      const sharedInput = dialog.querySelector("input[name='preset-shared']")
      const form = dialog.querySelector("form")
      const cancel = dialog.querySelector("button[data-action='cancel']")

      const finish = (value) => {
        dialog.close()
        dialog.remove()
        resolve(value)
      }
      cancel.addEventListener("click", () => finish(null))
      form.addEventListener("submit", (e) => {
        e.preventDefault()
        finish({ name: input.value, shared: sharedInput?.checked || false })
      })
      dialog.addEventListener("cancel", () => finish(null))
      dialog.showModal()
      requestAnimationFrame(() => input.focus())
    })
  }
}
